import React from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Chip,
  Tooltip,
  Link,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { getAdviserLink } from "../utils/adviserLinks";
import { fullName } from "../utils/investor";
import Empty from "../components/Empty";
import { Marker } from "../api/transformers/marker";
import { AssociatedPartiesItem, AssociatedPartyRoles } from "../api/transformers/associatedParty";
import { getOrganisationsParties } from "../api/organisationParty";
import ProfileContainer from "./ProfileContainer";
import { useNavigate } from "react-router";

type OrganisationPartyProfileProps = {
  personId: string;
  markers: Marker[];
};

const OrganisationPartyProfile: React.FC<OrganisationPartyProfileProps> = ({
  personId,
  markers,
}) => {
  const {
    data: organisationsPartiesData = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["organisations-parties", personId],
    queryFn: () => getOrganisationsParties(personId),
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <Box sx={{ width: "100%", height: 300, position: "relative" }}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (isError) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography color="error">Failed to load organisation links.</Typography>
      </Box>
    );
  }

  return (
    organisationsPartiesData.length > 0 && (
      <ProfileContainer
        title="Organisation Links"
        id="organisation-links-profile"
        loading={isLoading}
      >
        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ mb: 2 }}>
        Investor organisations this investor is linked to for AML purposes.
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Organisation</TableCell>
                  <TableCell>Roles</TableCell>
                  <TableCell>AML Status</TableCell>
                  <TableCell>Sanctions</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {organisationsPartiesData.map((item: AssociatedPartiesItem) => (
                  <OrganisationPartyRow key={item.investorId} data={item} markers={markers} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </ProfileContainer>
    )
  );
};

type OrganisationPartyRowProps = {
  data: AssociatedPartiesItem;
  markers: Marker[];
};

const OrganisationPartyRow: React.FC<OrganisationPartyRowProps> = ({
  data,
  markers,
}) => {
  const getMarkerDescription = (code: string, placeholder = "-") =>
    markers.find((el) => el.code === code)?.description || placeholder;

  const navigate = useNavigate();
  const handleViewAmlProfile = () => {
    navigate(`/investor-aml/${data.investorId}/profile`);
  };

  return (
    <TableRow>
      <TableCell>
        <span style={{ fontWeight: 700, marginRight: 10 }}>
          {fullName(data)}
        </span>
        <Tooltip title="View Investor Details">
          <Link
            href={getAdviserLink("investorDetail", {
              personId: data.investorId,
            })}
            target="_blank"
            rel="noopener noreferrer"
            color="primary"
            underline="hover"
            sx={{ fontStyle: "italic" }}
          >
            ({data.investorId})
          </Link>
        </Tooltip>
      </TableCell>
      <TableCell>
        <Chip
          size="small"
          label={data.roleDesc}
          sx={{ marginRight: "8px" }}
        />
      </TableCell>
      <TableCell>{getMarkerDescription(data.amlStatus)}</TableCell>
      <TableCell>{getMarkerDescription(data.sanctionsStatus)}</TableCell>
      <TableCell align="right">
        <Button variant="outlined" color="primary" sx={{ borderRadius: "4px" }} onClick={handleViewAmlProfile}>
          View AML Profile
        </Button>
      </TableCell>
    </TableRow>
  );
};

export default OrganisationPartyProfile;
